import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "ui/components/chart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "ui/components/card";
import ChartLoader from "@/components/loader/chart";
import { formatAmount, formatDateForAPI } from "utils/utils";
import { DisplayAmount } from "components/display/amount";
import { useClientRevenueChart } from "../hooks/useClientRevenueChart";
import { MonthRangePickerButton } from "components/month-range-picker-button";
import { usePersistentState } from "@/hooks/use-persistent-state";
import { generateYAxisTicks } from "@/lib/utils";
import { getLastFinancialQuarterRange } from "@/utils/financial-quarter";

const chartConfig = {
  contracted: {
    label: "Contracted",
    color: "#fda4af",
  },
  billed: {
    label: "Billed",
    color: "#fb7185",
  },
  collected: {
    label: "Collected",
    color: "#f43f5e",
  },
};

// Custom tick to handle long client names (ellipsis + full name on hover)
const MAX_TICK_LENGTH = 14;
const renderClientTick = (props) => {
  const { x, y, payload } = props;
  const full = payload?.value || "";
  const truncated =
    full.length > MAX_TICK_LENGTH
      ? `${full.slice(0, MAX_TICK_LENGTH - 1)}…`
      : full;

  return (
    
    <g transform={`translate(${x},${y})`}>
      <text
        dy={16}
        dx={-10}
        transform="rotate(-45)"
        textAnchor="end"
        fontSize={12}
      >
        <title>{full}</title>
        {truncated}
      </text>
    </g>
  );
};

export function ClientRevenueChart() {
  const [filters, setFilters] = usePersistentState({
    endpoint: "client-revenue-chart",
    defaultState: getLastFinancialQuarterRange(),
  });

  const handleMonthRangeSelect = (range) => {
    if (!range) {
      setFilters({
        ...filters,
        start_date: null,
        end_date: null,
      });
      return;
    }

    setFilters({
      ...filters,
      start_date: formatDateForAPI(range.start),
      end_date: formatDateForAPI(range.end),
    });
  };

  const { data, isLoading, error } = useClientRevenueChart({
    dateRange: {
      from: filters.start_date ? filters.start_date : null,
      to: filters.end_date ? filters.end_date : null,
    },
  });

  if (isLoading) {
    return <ChartLoader />;
  }

  if (error) {
    return (
      <Card className="mx-6 mt-4">
        <CardContent className="pt-6">
          <div className="text-center py-8 text-destructive">
            Error loading client revenue data: {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="mx-6 mt-4">
        <CardContent className="pt-6">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            No client revenue data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Flatten the data structure for easier chart consumption
  const flattenedData = data.map((item) => ({
    client: item.client,
    total_amount: item.total_amount,
    billableCount: item.billableCount,
    contracted: item.amount?.contracted || 0,
    billed: item.amount?.billed || 0,
    collected: item.amount?.collected || 0,
  }));

  // Calculate the maximum value from all amount types and add 10% padding
  const allValues = flattenedData.flatMap((item) => [
    item.contracted,
    item.billed,
    item.collected,
  ]);
  const maxValue = Math.max(...allValues);
  const yAxisMax = Math.ceil(maxValue * 1.1);

  const yAxisTicks = generateYAxisTicks(yAxisMax);

  return (
    <Card className="mx-6 mt-4 shadow-none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Client Revenue Distribution</CardTitle>
            <CardDescription>
              {filters.start_date && filters.end_date
                ? `Last 12 Months - Top performing clients by revenue`
                : `All Time - Top performing clients by revenue`}
            </CardDescription>
          </div>
          <MonthRangePickerButton
            selectedMonthRange={
              filters.start_date && filters.end_date
                ? {
                    start: new Date(filters.start_date),
                    end: new Date(filters.end_date),
                  }
                : null
            }
            onMonthRangeSelect={handleMonthRangeSelect}
            placeholder="Select month range"
            showQuickSelectors={true}
          />
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <BarChart
            accessibilityLayer
            data={flattenedData}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            barCategoryGap="10%"
            barGap={2}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="client"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              interval={0}
              height={90}
              tick={renderClientTick}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              fontSize={12}
              tickFormatter={formatAmount}
              domain={[0, yAxisMax]}
              ticks={yAxisTicks}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  hideLabel={false}
                  labelFormatter={(value, payload) =>
                    payload?.[0]?.payload?.client || ""
                  }
                  formatter={(value, name) => [
                    <DisplayAmount value={value} format={true} />,
                    chartConfig[name]?.label || name,
                  ]}
                />
              }
            />
            <ChartLegend content={<ChartLegendContent />} />
            <Bar
              dataKey="contracted"
              fill="var(--color-contracted)"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="billed"
              fill="var(--color-billed)"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="collected"
              fill="var(--color-collected)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
